<template>
  <div class="doc-pages-container">
    <!-- Header avec recherche -->
    <div class="doc-header mb-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="Title mb-0">Documentation</h1>
        <div class="header-actions">
            <b-form-input
            v-model="searchQuery"
            placeholder="Rechercher dans la documentation..."
            class="search-input"
            @input="debouncedSearch"
              @focus="focusSearch"
            debounce="500"
          />
        </div>
      </div>
    </div>

    <!-- Résultats de recherche -->
    <div v-if="searchMode && searchResults.length > 0" class="search-results mb-4">
      <h5 class="mb-3">Résultats de recherche ({{ searchResults.length }})</h5>
      <b-list-group>
        <b-list-group-item
          v-for="result in searchResults"
          :key="result.id"
          @click="navigateToPage(result.slug)"
          class="search-result-item"
          button
        >
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <h6 class="mb-1">{{ result.title }}</h6>
              <small class="text-muted">{{ result.slug }}</small>
            </div>
            <b-badge variant="secondary">{{ formatDate(result.updated_at) }}</b-badge>
          </div>
        </b-list-group-item>
      </b-list-group>
    </div>

    <!-- Message si aucun résultat de recherche -->
    <div v-if="searchMode && searchResults.length === 0 && !loading" class="text-center py-5">
      <i class="fas fa-search fa-3x text-muted mb-3"></i>
      <p class="text-muted">Aucun résultat trouvé pour "{{ searchQuery }}"</p>
    </div>

    <!-- Contenu principal : Navigation + Page -->
    <div v-if="!searchMode || searchResults.length === 0" class="doc-content">
      <div class="row">
        <!-- Sidebar Navigation -->
        <div class="col-md-3 col-12 mb-4 mb-md-0">
          <b-card class="doc-nav-card">
            <template #header>
              <h6 class="mb-0">
                <i class="fas fa-list me-2"></i>Navigation
              </h6>
            </template>
            <div v-if="loadingNav" class="text-center py-3">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Chargement...</span>
              </div>
            </div>
            <div v-else-if="navigation.length === 0" class="text-muted small">
              Aucune page disponible
            </div>
            <nav v-else class="doc-nav">
              <div
                v-if="rootIndexPage"
                class="nav-item nav-item-root"
                :class="{ active: currentPage?.slug === '' || currentPage?.slug === 'index' }"
                @click="navigateToPage('index')"
              >
                <i class="fas fa-home me-2"></i>
                {{ rootIndexTitle }}
              </div>
              <DocNavItem
                v-for="item in sidebarNavigation"
                :key="item.id"
                :item="item"
                :active-slug="currentPage?.slug"
                @navigate="navigateToPage"
              />
            </nav>
          </b-card>
        </div>

        <!-- Contenu de la page -->
        <div class="col-md-9 col-12">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
            <p class="mt-3">Chargement de la page...</p>
          </div>

          <div v-else-if="!currentPage" class="pages-list-container">
            <h4 class="mb-4">Pages de documentation</h4>
            <div v-if="navigation.length === 0" class="text-center py-5">
              <i class="fas fa-book fa-3x text-muted mb-3"></i>
              <p class="text-muted">Aucune page disponible</p>
            </div>
            <div v-else class="parent-pages-grid">
              <b-card
                v-if="rootIndexPage"
                class="parent-page-card mb-3"
                @click="navigateToPage('index')"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <h5 class="mb-2">
                      <i class="fas fa-home me-2 text-primary"></i>
                      {{ rootIndexTitle }}
                    </h5>
                    <p class="text-muted mb-2 small">Page d'accueil de la documentation</p>
                  </div>
                  <b-button variant="outline-primary" size="sm" @click.stop="navigateToPage('index')">
                    <i class="fas fa-arrow-right me-1"></i>Voir
                  </b-button>
                </div>
              </b-card>
              <b-card
                v-for="parentPage in cardNavigation"
                :key="parentPage.id"
                class="parent-page-card mb-3"
                @click="navigateToPage(parentPage.slug)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <h5 class="mb-2">
                      <i class="fas fa-folder-open me-2 text-primary"></i>
                      {{ parentPage.title }}
                    </h5>
                    <p v-if="parentPage.children && parentPage.children.length > 0" class="text-muted mb-2 small">
                      {{ parentPage.children.length }} page{{ parentPage.children.length > 1 ? 's' : '' }} disponible{{ parentPage.children.length > 1 ? 's' : '' }}
                    </p>
                    <p v-else class="text-muted mb-2 small">Page sans sous-pages</p>
                  </div>
                  <b-button variant="outline-primary" size="sm" @click.stop="navigateToPage(parentPage.slug)">
                    <i class="fas fa-arrow-right me-1"></i>Voir
                  </b-button>
                </div>
                
                <!-- Liste des pages enfants -->
                <div
                  v-if="parentPage.children && parentPage.children.length > 0"
                  class="children-pages mt-3 pt-3 border-top"
                >
                  <h6 class="mb-2 small text-muted">
                    <i class="fas fa-list me-1"></i>Pages disponibles :
                  </h6>
                  <div class="children-list">
                    <div
                      v-for="child in parentPage.children"
                      :key="child.id"
                      class="child-page-item"
                      @click.stop="navigateToPage(child.slug)"
                    >
                      <i class="fas fa-file-alt me-2 text-secondary"></i>
                      <span>{{ child.title }}</span>
                      <i class="fas fa-chevron-right ms-auto text-muted"></i>
                    </div>
                  </div>
                </div>
              </b-card>
            </div>
          </div>

          <b-card v-else class="doc-page-card">
            <template #header>
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2">
                  <b-button variant="outline-secondary" size="sm" @click="navigateToDocsHome">
                    <i class="fas fa-arrow-left me-1"></i>Liste des docs
                  </b-button>
                  <h4 class="mb-0">{{ currentPage.title }}</h4>
                </div>
                <small class="text-muted">
                  <i class="fas fa-clock me-1"></i>
                  Mis à jour le {{ formatDate(currentPage.updated_at) }}
                </small>
              </div>
            </template>
            <div class="doc-content-body" v-html="renderedContent" @click="handleDocLinkClick"></div>
            <template #footer v-if="currentPage.created_by_name">
              <small class="text-muted">
                <i class="fas fa-user me-1"></i>
                Créé par {{ currentPage.created_by_name }}
              </small>
            </template>
          </b-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/plugins/api';
import { marked, Renderer } from 'marked';

// Composant récursif pour la navigation
const DocNavItem = {
  name: 'DocNavItem',
  props: {
    item: {
      type: Object,
      required: true
    },
    activeSlug: {
      type: String,
      default: null
    }
  },
  methods: {
    handleClick() {
      this.$emit('navigate', this.item.slug);
    }
  },
  template: `
    <div class="nav-item-wrapper">
      <div
        class="nav-item"
        :class="{ active: activeSlug === item.slug }"
        @click="handleClick"
      >
        <i class="fas fa-file-alt me-2"></i>
        {{ item.title }}
      </div>
      <div v-if="item.children && item.children.length > 0" class="nav-children">
        <DocNavItem
          v-for="child in item.children"
          :key="child.id"
          :item="child"
          :active-slug="activeSlug"
          @navigate="$emit('navigate', $event)"
        />
      </div>
    </div>
  `
};

export default {
  name: 'DocPagesPage',
  components: {
    DocNavItem
  },
  data() {
    return {
      navigation: [],
      currentPage: null,
      searchQuery: '',
      searchResults: [],
      searchMode: false,
      loading: false,
      loadingNav: false,
      version: 'latest',
      searchTimeout: null
    };
  },
  computed: {
    rootIndexPage() {
      return this.navigation.find(p => p.slug === '') || null;
    },
    rootIndexTitle() {
      if (!this.rootIndexPage) return 'Accueil';
      return this.rootIndexPage.title && this.rootIndexPage.title !== 'Sans titre'
        ? this.rootIndexPage.title
        : 'Accueil';
    },
    indexOnlyMode() {
      return this.navigation.some(p => p.metadata?.is_index);
    },
    currentTreeRoot() {
      if (!this.currentPage?.slug) return null;
      return this.findAncestorIndex(this.navigation, this.currentPage.slug);
    },
    sidebarNavigation() {
      if (this.currentTreeRoot?.children?.length) {
        return this.currentTreeRoot.children;
      }
      if (this.rootIndexPage?.children?.length) {
        return this.rootIndexPage.children;
      }
      return this.navigation;
    },
    cardNavigation() {
      // Page d'accueil: afficher les index de premier niveau
      const baseItems = this.rootIndexPage?.children?.length
        ? this.rootIndexPage.children
        : this.navigation;
      const filtered = this.filterIndexTree(baseItems);
      if (this.rootIndexPage) {
        return filtered.filter(item => item.slug !== '');
      }
      return filtered;
    },
    renderedContent() {
      if (!this.currentPage?.content_md) {
        return '<p class="text-muted">Aucun contenu disponible</p>';
      }
      try {
        // Créer un renderer personnalisé pour les liens
        const renderer = new Renderer();
        const originalLink = renderer.link.bind(renderer);
        
        renderer.link = (href, title, text) => {
          let linkHref = href;
          let linkTitle = title;
          let linkText = text;

          if (href && typeof href === 'object') {
            linkHref = href.href;
            linkTitle = href.title;
            linkText = href.text || text;
          }

          if (!linkHref || typeof linkHref !== 'string') {
            return originalLink(href, title, text);
          }

          // Si c'est un lien relatif (pas http/https), le convertir en lien vers la doc
          if (!linkHref.startsWith('http://') && !linkHref.startsWith('https://') && !linkHref.startsWith('#')) {
            // Nettoyer le href (enlever .md, ./ , ../, etc.)
            let cleanHref = linkHref
              .replace(/^\.\//, '') // Enlever ./
              .replace(/^\.\.\//, '') // Enlever ../
              .replace(/\.md$/, '') // Enlever .md
              .replace(/\.mdx$/, '') // Enlever .mdx
              .replace(/\/$/, ''); // Enlever le slash final
            
            // Si le lien commence par /, l'enlever
            if (cleanHref.startsWith('/')) {
              cleanHref = cleanHref.substring(1);
            }
            
            // Construire le lien vers la page de doc
            const docHref = `#/docs/${cleanHref}`;
            return `<a href="${docHref}" class="doc-link" data-slug="${cleanHref}" title="${linkTitle || ''}">${linkText || ''}</a>`;
          }
          
          // Pour les liens absolus, utiliser le comportement par défaut
          return originalLink(href, title, text);
        };
        
        return marked(this.currentPage.content_md, {
          breaks: true,
          gfm: true,
          renderer: renderer
        });
      } catch (error) {
        console.error('Erreur lors du rendu Markdown:', error);
        return '<p class="text-danger">Erreur lors du rendu du contenu</p>';
      }
    }
  },
  watch: {
    '$route'(to) {
      if (to.params.slug !== undefined) {
        this.loadPage(to.params.slug);
        return;
      }
      this.currentPage = null;
    }
  },
  methods: {
    findNodeBySlug(items, slug, trail = []) {
      for (const item of items || []) {
        if (item.slug === slug) {
          return { node: item, trail: [...trail, item] };
        }
        if (item.children && item.children.length > 0) {
          const result = this.findNodeBySlug(item.children, slug, [...trail, item]);
          if (result) return result;
        }
      }
      return null;
    },
    findAncestorIndex(items, slug) {
      const found = this.findNodeBySlug(items, slug);
      if (!found) return null;
      // Chercher le plus proche ancêtre "index"
      for (let i = found.trail.length - 1; i >= 0; i -= 1) {
        if (this.isIndexPage(found.trail[i])) {
          return found.trail[i];
        }
      }
      return null;
    },
    isIndexPage(item) {
      return item?.metadata?.is_index === true || item?.slug === '';
    },
    filterIndexTree(items) {
      return (items || [])
        .filter(item => this.isIndexPage(item))
        .map(item => ({
          ...item,
          children: this.filterIndexTree(item.children || [])
        }));
    },
    async loadNavigation() {
      this.loadingNav = true;
      try {
        const response = await api.get('/doc-pages/navigation', {
          params: { version: this.version }
        });
        this.navigation = response.data.data || [];
      } catch (error) {
        console.error('Erreur lors du chargement de la navigation:', error);
        this.navigation = [];
      } finally {
        this.loadingNav = false;
      }
    },
    async loadPage(slug) {
      if (slug === undefined || slug === null) {
        this.currentPage = null;
        return;
      }

      this.loading = true;
      try {
        const isRootIndex = slug === '' || slug === 'index';
        const encodedSlug = isRootIndex
          ? ''
          : String(slug)
            .split('/')
            .map(segment => encodeURIComponent(segment))
            .join('/');
        const url = isRootIndex ? '/doc-pages/slug' : `/doc-pages/slug/${encodedSlug}`;
        const response = await api.get(url, {
          params: { version: this.version }
        });
        this.currentPage = response.data.data;
        this.searchMode = false;
      } catch (error) {
        console.error('Erreur lors du chargement de la page:', error);
        if (error.response?.status === 404) {
          this.currentPage = null;
        }
      } finally {
        this.loading = false;
      }
    },
    async navigateToPage(slug) {
      const targetSlug = (slug === '' || slug === null || slug === undefined) ? 'index' : slug;
      try {
        await this.$router.push({ name: 'DocPages', params: { slug: targetSlug } });
      } catch (error) {
        // Si la navigation est dupliquée, on force quand même le chargement
        if (error?.name !== 'NavigationDuplicated') {
          console.error('Erreur navigation docs:', error);
        }
      }
      if (this.$route.params.slug === targetSlug) {
        await this.loadPage(targetSlug);
      }
    },
    async navigateToDocsHome() {
      try {
        await this.$router.push({ name: 'DocPages' });
      } catch (error) {
        if (error?.name !== 'NavigationDuplicated') {
          console.error('Erreur navigation docs:', error);
        }
      }
      if (this.$route.params.slug === undefined) {
        this.currentPage = null;
      }
    },
    async performSearch() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.searchMode = false;
        this.searchResults = [];
        return;
      }

      this.loading = true;
      try {
        const response = await api.get('/doc-pages/search', {
          params: {
            q: this.searchQuery.trim(),
            version: this.version
          }
        });
        this.searchResults = response.data.data || [];
        this.searchMode = true;
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        this.searchResults = [];
      } finally {
        this.loading = false;
      }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 500);
    },
    focusSearch() {
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        this.performSearch();
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    findFirstPage(items) {
      // Chercher d'abord un index.md (slug vide ou "index")
      for (const item of items) {
        if (item.slug === '' || item.slug === 'index') {
          return item;
        }
      }
      
      // Sinon, retourner null pour ne pas charger automatiquement
      // L'utilisateur verra la liste des pages mères
      return null;
    },
    handleDocLinkClick(event) {
      // Gérer les clics sur les liens de documentation
      const link = event.target.closest('.doc-link');
      if (link) {
        event.preventDefault();
        event.stopPropagation();
        const slug = link.getAttribute('data-slug') || link.getAttribute('href')?.replace('#/docs/', '');
        if (slug) {
          this.navigateToPage(slug);
        }
      }
    }
  },
  async mounted() {
    await this.loadNavigation();
    
    // Charger la page si un slug est présent dans l'URL
    const slug = this.$route.params.slug;
    if (slug) {
      await this.loadPage(slug);
    } else {
      // Ne pas charger automatiquement de page
      // L'utilisateur verra la liste des pages mères
      // Si un index.md existe (slug vide), il sera affiché dans la liste
      this.currentPage = null;
    }
  }
};
</script>

<style scoped>
.doc-pages-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
}

.doc-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 1rem;
}

.search-input {
  max-width: 400px;
}

.search-results {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.search-result-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.search-result-item:hover {
  background-color: #e9ecef;
}

.doc-nav-card {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
}

.doc-nav {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item-wrapper {
  margin-bottom: 0.25rem;
}

.nav-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
  color: #495057;
  font-size: 0.9rem;
}

.nav-item:hover {
  background-color: #f8f9fa;
}

.nav-item.active {
  background-color: #007bff;
  color: white;
  font-weight: 600;
}

.nav-children {
  margin-left: 1rem;
  margin-top: 0.25rem;
  border-left: 2px solid #e9ecef;
  padding-left: 0.5rem;
}

.doc-page-card {
  min-height: 400px;
}

.doc-content-body {
  line-height: 1.8;
}

.doc-content-body :deep(h1),
.doc-content-body :deep(h2),
.doc-content-body :deep(h3),
.doc-content-body :deep(h4) {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.doc-content-body :deep(h1) {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.doc-content-body :deep(p) {
  margin-bottom: 1rem;
}

.doc-content-body :deep(code) {
  background-color: #f4f4f4;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.9em;
  color: #e83e8c;
}

.doc-content-body :deep(pre) {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  border-left: 4px solid #007bff;
}

.doc-content-body :deep(pre code) {
  background-color: transparent;
  padding: 0;
  color: #333;
}

.doc-content-body :deep(blockquote) {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
  margin-left: 0;
  color: #6c757d;
  font-style: italic;
}

.doc-content-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.doc-content-body :deep(table th),
.doc-content-body :deep(table td) {
  border: 1px solid #dee2e6;
  padding: 0.75rem;
  text-align: left;
}

.doc-content-body :deep(table th) {
  background-color: #f8f9fa;
  font-weight: 600;
}

.doc-content-body :deep(ul),
.doc-content-body :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

.doc-content-body :deep(li) {
  margin-bottom: 0.5rem;
}

.doc-content-body :deep(a) {
  color: #007bff;
  text-decoration: none;
  transition: color 0.2s;
}

.doc-content-body :deep(a:hover) {
  text-decoration: underline;
  color: #0056b3;
}

.doc-content-body :deep(a.doc-link) {
  color: #007bff;
  cursor: pointer;
  font-weight: 500;
}

.doc-content-body :deep(a.doc-link:hover) {
  color: #0056b3;
  text-decoration: underline;
}

.doc-content-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 1rem 0;
}

.pages-list-container {
  padding: 1rem 0;
}

.parent-pages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.parent-page-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #dee2e6;
}

.parent-page-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #007bff;
}

.parent-page-card h5 {
  color: #2c3e50;
  font-weight: 600;
}

.children-pages {
  margin-top: 1rem;
}

.children-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.child-page-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.child-page-item:hover {
  background-color: #e9ecef;
  transform: translateX(4px);
}

.child-page-item span {
  flex: 1;
  color: #495057;
}

@media (max-width: 768px) {
  .doc-header .d-flex {
    flex-direction: column;
    gap: 1rem;
  }

  .search-input {
    max-width: 100%;
  }

  .doc-nav-card {
    position: relative;
    top: 0;
    max-height: none;
  }

  .parent-pages-grid {
    grid-template-columns: 1fr;
  }
}
</style>

